import React from 'react';
import { Check, Star, ArrowRight, Phone, Shield, Clock, FileCheck, MessageCircle, Award } from 'lucide-react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ScrollReveal } from './ScrollAnimations';

const Pricing = () => {
  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) element.scrollIntoView({ behavior: 'smooth' });
  };

  const promises = [
    {
      id: 'transparent-payments',
      icon: Shield,
      title: 'Transparent Payments',
      gradient: 'from-green-500 to-emerald-500',
      points: [
        'No hidden charges — jo final hoga wahi pay karna hoga',
        'Clear advance policy (30–50%) before project start',
        'Remaining payment only after your approval'
      ]
    },
    {
      id: 'on-time-delivery',
      icon: Clock,
      title: 'On-Time Delivery',
      gradient: 'from-blue-500 to-cyan-500',
      points: [
        'Agreed timeline ke andar project delivery',
        'Har stage pe progress update',
        'Delay hone par pehle se inform karna (no surprises)'
      ]
    },
    {
      id: 'written-commitment',
      icon: FileCheck,
      title: 'Written Commitment',
      gradient: 'from-purple-500 to-pink-500',
      points: [
        'Project scope, cost & delivery sab written me confirm',
        'Revisions & features clearly mentioned',
        'Mutual clarity before starting the work'
      ]
    },
    {
      id: 'professional-communication',
      icon: MessageCircle,
      title: 'Professional Communication',
      gradient: 'from-orange-500 to-amber-500',
      points: [
        'Dedicated WhatsApp & Email support',
        'Clear, respectful & timely responses',
        'No confusing technical language'
      ]
    },
    {
      id: 'quality-support',
      icon: Award,
      title: 'Quality & Support Assurance',
      gradient: 'from-indigo-500 to-purple-500',
      points: [
        'Mobile-friendly & performance-optimized websites',
        'Bug-free final delivery check',
        'Free support for agreed time period after delivery'
      ]
    }
  ];

  const pricingPlans = [
    {
      id: 'starter',
      name: 'Starter',
      price: '₹1,999',
      priceNote: 'Starting from',
      description: 'Best for individuals and small businesses',
      features: [
        'Landing / single page website',
        'Mobile responsive design',
        'Contact form',
        'Basic UI design',
        'Free consultation'
      ],
      cta: 'Get Quote',
      highlighted: false,
      gradient: 'from-blue-500 to-cyan-500'
    },
    {
      id: 'business',
      name: 'Business',
      price: '₹4,999',
      priceNote: 'Starting from',
      description: 'For growing businesses with more requirements',
      badge: 'Most Popular',
      features: [
        '5–10 page website',
        'Premium UI/UX design',
        'WhatsApp integration',
        'SEO-ready structure',
        'Speed optimization',
        '1 month support'
      ],
      cta: 'Discuss Requirements',
      highlighted: true,
      gradient: 'from-purple-500 to-pink-500'
    },
    {
      id: 'premium',
      name: 'Premium',
      price: 'Custom Pricing',
      priceNote: '',
      description: 'For advanced and large business needs',
      features: [
        'Custom website design',
        'Unlimited pages',
        'Admin panel / CMS',
        'Payment gateway integration',
        'Advanced security',
        'Long-term support'
      ],
      cta: 'Book Free Call',
      highlighted: false,
      gradient: 'from-indigo-500 to-purple-500'
    }
  ];

  return (
    <section id="pricing" className="py-24 bg-gray-100 dark:bg-gray-900 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-gradient-to-br from-blue-200 to-purple-200 dark:from-blue-900/20 dark:to-purple-900/20 rounded-full filter blur-3xl opacity-30"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-gradient-to-tl from-purple-200 to-pink-200 dark:from-purple-900/20 dark:to-pink-900/20 rounded-full filter blur-3xl opacity-30"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Section Header */}
        <ScrollReveal>
          <div className="text-center mb-16">
            <motion.div
              className="inline-flex items-center space-x-2 bg-white/80 dark:bg-gray-800/80 backdrop-blur-lg text-blue-700 dark:text-blue-300 px-5 py-2.5 rounded-full mb-6 border border-blue-200 dark:border-blue-800 shadow-lg"
              initial={{ scale: 0.9, opacity: 0 }}
              whileInView={{ scale: 1, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <Star className="w-4 h-4 fill-current" />
              <span className="text-sm font-semibold">Transparent Pricing</span>
            </motion.div>

            <motion.h2
              className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              Choose Your Perfect Plan
            </motion.h2>

            <motion.p
              className="text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              Flexible pricing based on your requirements. No hidden charges, honest pricing.
            </motion.p>
          </div>
        </ScrollReveal>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
          {pricingPlans.map((plan, index) => (
            <motion.div
              key={plan.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <motion.div
                whileHover={{ y: -12, scale: plan.highlighted ? 1.05 : 1.03 }}
                transition={{ duration: 0.3, ease: "easeOut" }}
              >
                <Card
                  className={`relative overflow-hidden h-full ${
                    plan.highlighted
                      ? 'border-4 border-purple-500 dark:border-purple-400 shadow-2xl'
                      : 'border-2 border-gray-200 dark:border-gray-700 shadow-xl'
                  } bg-white dark:bg-gray-800 hover:shadow-2xl transition-all duration-300`}
                >
                  {/* Badge for Most Popular */}
                  {plan.badge && (
                    <div className="absolute top-0 right-0">
                      <div className="bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs font-bold px-4 py-2 rounded-bl-2xl rounded-tr-xl shadow-lg">
                        <div className="flex items-center space-x-1">
                          <Star className="w-3 h-3 fill-current" />
                          <span>{plan.badge}</span>
                        </div>
                      </div>
                    </div>
                  )}

                  <CardHeader className="text-center pb-8 pt-8">
                    {/* Plan Icon */}
                    <motion.div
                      className={`w-16 h-16 bg-gradient-to-br ${plan.gradient} rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg`}
                      whileHover={{ rotate: 360, scale: 1.1 }}
                      transition={{ duration: 0.6 }}
                    >
                      <div className="text-white text-2xl font-bold">
                        {plan.name.charAt(0)}
                      </div>
                    </motion.div>

                    <CardTitle className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                      {plan.name}
                    </CardTitle>

                    {/* Price */}
                    <div className="my-6">
                      {plan.priceNote && (
                        <div className="text-sm text-gray-500 dark:text-gray-400 mb-1">
                          {plan.priceNote}
                        </div>
                      )}
                      <div className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 dark:from-white dark:to-gray-300 bg-clip-text text-transparent">
                        {plan.price}
                      </div>
                    </div>

                    <CardDescription className="text-base text-gray-600 dark:text-gray-400">
                      {plan.description}
                    </CardDescription>
                  </CardHeader>

                  <CardContent className="px-6 pb-8">
                    {/* Features List */}
                    <ul className="space-y-4 mb-8">
                      {plan.features.map((feature, idx) => (
                        <motion.li
                          key={idx}
                          className="flex items-start space-x-3"
                          initial={{ opacity: 0, x: -20 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.4, delay: idx * 0.1 }}
                        >
                          <div className={`flex-shrink-0 w-5 h-5 rounded-full bg-gradient-to-br ${plan.gradient} flex items-center justify-center mt-0.5`}>
                            <Check className="w-3 h-3 text-white" />
                          </div>
                          <span className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
                            {feature}
                          </span>
                        </motion.li>
                      ))}
                    </ul>

                    {/* CTA Button */}
                    <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.98 }}>
                      <Button
                        size="lg"
                        onClick={scrollToContact}
                        className={`w-full ${
                          plan.highlighted
                            ? 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white shadow-xl'
                            : 'bg-gradient-to-r from-gray-900 to-gray-700 hover:from-gray-800 hover:to-gray-600 dark:from-white dark:to-gray-200 dark:hover:from-gray-100 dark:hover:to-gray-300 text-white dark:text-gray-900 shadow-lg'
                        } transition-all duration-300`}
                      >
                        {plan.cta}
                        {plan.id === 'premium' ? (
                          <Phone className="ml-2 h-4 w-4" />
                        ) : (
                          <ArrowRight className="ml-2 h-4 w-4" />
                        )}
                      </Button>
                    </motion.div>
                  </CardContent>
                </Card>
              </motion.div>
            </motion.div>
          ))}
        </div>

        {/* Trust Note */}
        <ScrollReveal delay={0.3}>
          <motion.div
            className="max-w-3xl mx-auto text-center bg-white/80 dark:bg-gray-800/80 backdrop-blur-lg rounded-2xl p-8 border border-gray-200 dark:border-gray-700 shadow-xl"
            whileHover={{ scale: 1.02, y: -5 }}
            transition={{ duration: 0.3 }}
          >
            <div className="flex items-center justify-center mb-4">
              <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-500 rounded-full flex items-center justify-center">
                <Check className="w-6 h-6 text-white" />
              </div>
            </div>
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
              Honest & Transparent Pricing
            </h3>
            <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
              <span className="font-semibold">Final pricing depends on features, pages, and custom requirements.</span>
              <br />
              No hidden charges. Free consultation included with every project.
            </p>
          </motion.div>
        </ScrollReveal>

        {/* Our Promises Section */}
        <div className="mt-20">
          <ScrollReveal>
            <div className="text-center mb-12">
              <motion.div
                className="inline-flex items-center space-x-2 bg-white/80 dark:bg-gray-800/80 backdrop-blur-lg text-green-700 dark:text-green-300 px-5 py-2.5 rounded-full mb-6 border border-green-200 dark:border-green-800 shadow-lg"
                initial={{ scale: 0.9, opacity: 0 }}
                whileInView={{ scale: 1, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
              >
                <Shield className="w-4 h-4" />
                <span className="text-sm font-semibold">Our Promises</span>
              </motion.div>

              <motion.h3
                className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.1 }}
              >
                Payment & Professionalism
              </motion.h3>

              <motion.p
                className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                We believe in trust, transparency, and delivering quality work on time
              </motion.p>
            </div>
          </ScrollReveal>

          {/* Promises Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {promises.map((promise, index) => {
              const IconComponent = promise.icon;
              return (
                <motion.div
                  key={promise.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <motion.div
                    whileHover={{ y: -8, scale: 1.03 }}
                    transition={{ duration: 0.3 }}
                  >
                    <Card className="h-full bg-white/80 dark:bg-gray-800/80 backdrop-blur-lg border-2 border-gray-200 dark:border-gray-700 hover:shadow-2xl transition-all duration-300 overflow-hidden group">
                      <CardHeader className="text-center pb-4">
                        {/* Icon with gradient background */}
                        <motion.div
                          className={`w-16 h-16 bg-gradient-to-br ${promise.gradient} rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg group-hover:shadow-xl`}
                          whileHover={{ rotate: [0, -10, 10, -10, 0], scale: 1.1 }}
                          transition={{ duration: 0.5 }}
                        >
                          <IconComponent className="w-8 h-8 text-white" />
                        </motion.div>

                        <CardTitle className="text-xl font-bold text-gray-900 dark:text-white">
                          {promise.title}
                        </CardTitle>
                      </CardHeader>

                      <CardContent className="px-6 pb-6">
                        <ul className="space-y-3">
                          {promise.points.map((point, idx) => (
                            <motion.li
                              key={idx}
                              className="flex items-start space-x-3"
                              initial={{ opacity: 0, x: -20 }}
                              whileInView={{ opacity: 1, x: 0 }}
                              viewport={{ once: true }}
                              transition={{ duration: 0.4, delay: idx * 0.1 }}
                            >
                              <div className={`flex-shrink-0 w-5 h-5 rounded-full bg-gradient-to-br ${promise.gradient} flex items-center justify-center mt-0.5`}>
                                <Check className="w-3 h-3 text-white" />
                              </div>
                              <span className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                                {point}
                              </span>
                            </motion.li>
                          ))}
                        </ul>
                      </CardContent>

                      {/* Hover gradient overlay */}
                      <motion.div
                        className={`absolute inset-0 bg-gradient-to-br ${promise.gradient} opacity-0 group-hover:opacity-5 transition-opacity duration-300 pointer-events-none`}
                      />
                    </Card>
                  </motion.div>
                </motion.div>
              );
            })}
          </div>

          {/* Trust Badge */}
          <ScrollReveal delay={0.2}>
            <motion.div
              className="max-w-2xl mx-auto text-center bg-gradient-to-r from-green-50 via-emerald-50 to-teal-50 dark:from-green-900/20 dark:via-emerald-900/20 dark:to-teal-900/20 rounded-2xl p-8 border-2 border-green-200/50 dark:border-green-800/50 backdrop-blur-lg shadow-xl"
              whileHover={{ scale: 1.02 }}
              transition={{ duration: 0.3 }}
            >
              <div className="flex items-center justify-center space-x-3 mb-4">
                <motion.div
                  className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-500 rounded-full flex items-center justify-center"
                  whileHover={{ rotate: 360 }}
                  transition={{ duration: 0.6 }}
                >
                  <Shield className="w-5 h-5 text-white" />
                </motion.div>
                <h4 className="text-xl font-bold text-gray-900 dark:text-white">
                  100% Client Satisfaction Guarantee
                </h4>
              </div>
              <p className="text-gray-700 dark:text-gray-300">
                Your trust is our biggest asset. Hum apne promises deliver karte hain.
              </p>
            </motion.div>
          </ScrollReveal>
        </div>

        {/* Additional CTA */}
        <ScrollReveal delay={0.4}>
          <div className="text-center mt-12">
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Not sure which plan fits your needs?
            </p>
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.98 }}>
              <Button
                size="lg"
                variant="outline"
                onClick={scrollToContact}
                className="border-2 border-blue-600 text-blue-600 hover:bg-blue-50 dark:hover:bg-gray-800 backdrop-blur-lg px-8"
              >
                <Phone className="mr-2 h-5 w-5" />
                Talk to Our Team
              </Button>
            </motion.div>
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
};

export default Pricing;
